import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationContext';
import type { BulkReleaseHandle, BulkScope } from '@/hooks/workbench/bulkRelease';
import { useWorkbench, DEFAULT_FILTERS, type WorkbenchEditState, type WorkbenchFilters } from '@/hooks/useWorkbench';
import {
  isAllReviewedEmptyState,
  isNothingToReviewEmptyState,
  isNoProductsEmptyState,
  isAutoLoadCtaVisible,
} from '@/hooks/workbench/emptyStateGates';
import { WorkbenchProgress } from './WorkbenchProgress';
import { WorkbenchProductCard } from './WorkbenchProductCard';
import { WorkbenchActionBar } from './WorkbenchActionBar';
import { WorkbenchEmptyState } from './WorkbenchEmptyState';
import { WorkbenchFilterBar } from './WorkbenchFilterBar';
import { useRailWidthPreference } from '@/hooks/workbench/railWidthPreference';
import {
  useStatusPreference,
  readStatusFromSearch,
  writeStatusPreference,
  hasAwaitingUploadScope,
  AWAITING_UPLOAD_SCOPE,
  readRuleFromSearch,
  RULE_PARAM,
  type WorkbenchStatus,
} from '@/hooks/workbench/statusScope';
import { actionSetFor } from '@/hooks/workbench/statusActions';
import { useWorkbenchStatusCounts } from '@/hooks/useWorkbenchStatusCounts';
import { WorkbenchStatusTabs } from './WorkbenchStatusTabs';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { useErfolgsmomentAvgScore } from '@/hooks/workbench/useErfolgsmomentAvgScore';
import { cn } from '@/lib/utils';
import { WorkbenchRail } from './WorkbenchRail';
import { useIsNarrowViewport } from '@/hooks/workbench/useIsNarrowViewport';
import { registerPendingEditsGuard } from '@/lib/pendingEditsGuard';
import { ChevronLeft } from 'lucide-react';
import WorkbenchListView from './WorkbenchListView';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ErrorState';

interface WorkbenchReviewerProps {
  feedId: string;
  /**
   * Freigegeben und noch nicht bei Google. Reicht die Seite durch, weil der
   * Reviewer keine Feed-Statistik liest — und nur damit laesst sich der
   * Leerzustand "nichts zu pruefen" von seinem Zwilling "alles freigegeben,
   * wartet auf Upload" unterscheiden. Ohne die Angabe verhaelt sich der
   * Leerzustand wie bisher.
   */
  approvedAwaitingUpload?: number;
  /**
   * Nur fuer die einzeilige Kopfzone: die Aktionsknoepfe der Seite
   * (Sammelfreigabe, Upload). Sie stehen sonst im PageHeader; in der einen
   * Zeile gibt es den nicht mehr, und der Reviewer nimmt sie mit auf.
   */
  aktionen?: React.ReactNode;
  /**
   * Meldet der Seite eine Funktion, die eine offene Handkorrektur wegschreibt.
   *
   * Der Upload-Knopf sitzt auf Seitenebene (`WorkbenchUploadAction`) und kann
   * den Bearbeitungszustand der Karte nicht sehen. Ohne diese Bruecke ging der
   * ALTE Text zu Google, waehrend die Karte den neuen als das auswies, was
   * hochgeladen wuerde.
   */
  registerFlushPendingEdits?: (fn: (() => Promise<boolean>) | null) => void;
  /**
   * Meldet der Seite das Buendel hinter dem Knopf "Freigeben" (Spec 9.1).
   *
   * Der Knopf gehoert nach oben neben den Upload, der Zustand dahinter
   * entsteht hier: nur der Reviewer kennt Reiter, Suche und die
   * Speicher-Reihenfolge. Anders als `registerFlushPendingEdits` faehrt die
   * Seite damit einen STATE und keine Ref - die Zahlen im Menue muessen die
   * Kopfzeile neu rendern, eine Ref taete das nicht.
   */
  registerBulkRelease?: (handle: BulkReleaseHandle | null) => void;
  /**
   * Oeffnet den Upload-Dialog der SEITE (Spec 2026-09-16, Abschnitt 7). Die
   * Leerzustaende "alles freigegeben" und "alles geprueft" bieten damit den
   * naechsten Schritt an Ort und Stelle an, statt per Text in einen anderen
   * Reiter zu verweisen.
   */
  onUpload?: () => void;
}

function buildEditState(
  optimized_title: string | null,
  optimized_description: string | null,
  optimized_product_highlights: string[] | null,
  optimized_color: string | null,
  optimized_material: string | null,
  optimized_gender: string | null,
  optimized_age_group: string | null,
  optimized_size: string | null,
  optimized_pattern: string | null,
  optimized_google_product_category: string | null,
): WorkbenchEditState {
  return {
    optimized_title: optimized_title ?? '',
    optimized_description: optimized_description ?? '',
    optimized_product_highlights: optimized_product_highlights ?? [],
    optimized_color: optimized_color ?? '',
    optimized_material: optimized_material ?? '',
    optimized_gender: optimized_gender ?? '',
    optimized_age_group: optimized_age_group ?? '',
    optimized_size: optimized_size ?? '',
    optimized_pattern: optimized_pattern ?? '',
    optimized_google_product_category: optimized_google_product_category ?? '',
  };
}

/**
 * Verengt irgendeine Dimension die sichtbare Menge?
 *
 * `warningsOnly` MUSS hier mitzaehlen. Die beiden Leerzustands-Tore
 * (`isNothingToReviewEmptyState`, `isAllReviewedEmptyState`) benutzen diese
 * Antwort, um "der Feed ist durch" von "der Filter hat alles versteckt" zu
 * unterscheiden. Fehlte die neue Dimension, behauptete die Werkbank nach
 * einem Klick auf "N mit Hinweisen zuerst ansehen" auf einem sauberen
 * Ausschnitt "Noch nichts zu pruefen" und schickte den Nutzer mit dem
 * Primaerknopf aus der Werkbank hinaus, obwohl tausende Produkte offen sind.
 *
 * `awaitingUploadOnly` (Task 5, Spec 5.2) zaehlt aus demselben Grund mit: der
 * Filter kommt per Deep-Link (`scope=awaiting_upload`) von aussen und kann
 * eine Menge genauso leer filtern wie die anderen drei Dimensionen — der
 * Leerzustand `no-filter-results` mit "Filter zuruecksetzen" muss ihn
 * deshalb sehen.
 */
const hasActiveFilters = (f: WorkbenchFilters) =>
  f.search !== ''
  || f.scoreFilter !== 'all'
  || f.warningsOnly
  || f.awaitingUploadOnly
  // WP5 A2O-02: eine Regel aus dem Feed-Check kann die Menge genauso leer
  // filtern; der Leerzustand muss dann "Filter zuruecksetzen" anbieten.
  || !!f.ruleId;

export function WorkbenchReviewer({
  feedId,
  approvedAwaitingUpload = 0,
  onUpload,
  aktionen,
  registerFlushPendingEdits,
  registerBulkRelease,
}: WorkbenchReviewerProps) {
  const { t } = useTranslation('workbench');

  /* Klassische oder einzeilige Kopfzone. Ausserhalb der Entwurfsseite liefert
     der Haken 'klassisch' und 'oben', also genau das heutige Verhalten. */
  const { kopfzone, fortschritt, listenKachel, leerzustand, leistenBreite } = useEntwurf();
  const eigeneKachel = listenKachel === 'eigeneKachel';
  const eineZeile = kopfzone === 'eineZeile';
  const { toast } = useNotifications();
  /**
   * `t` entsteht bei jedem Render neu (und in den Tests garantiert). Als
   * Abhaengigkeit einer useCallback wanderte damit die Identitaet des
   * Sammelfreigabe-Buendels pro Render, und die Anmeldung bei der Seite liefe
   * endlos: Anmeldung setzt Seiten-State, Seiten-State rendert den Reviewer,
   * neues `t`, neue Anmeldung. Der Ref haelt immer die aktuelle Fassung.
   */
  const tRef = useRef(t);
  tRef.current = t;
  /**
   * Reiter und Filter als Deep-Link (JourneyBar/Statusfluss Task 5, Spec
   * 5.1/5.2). Ein Journey-Chip fuehrt auf
   * `/workbench?status=pending&scope=awaiting_upload`.
   *
   * Die Suchparameter stehen VOR den Zustaenden, damit beide ihren Anfangswert
   * daraus nehmen koennen. Nachgezogen per Effect fragte die Werkbank ihre
   * Produktmenge zweimal an: einmal mit Praeferenz und ohne Filter, einmal mit
   * dem, was in der Adresse steht.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const startScope = hasAwaitingUploadScope(searchParams);
  const startRule = readRuleFromSearch(searchParams);
  const [filters, setFilters] = useState<WorkbenchFilters>(() => ({
    ...DEFAULT_FILTERS,
    awaitingUploadOnly: startScope,
    ruleId: startRule,
  }));
  // Der Modus startete hart auf 'card' und wurde nirgends gemerkt: jeder
  // Routenwechsel warf einen Nutzer mit 69.000 offenen Produkten zurueck ins
  // Einzelreview. Jetzt pro Feed gemerkt (siehe railWidthPreference.ts).
  //
  // Task B2: aus zwei Modi sind drei Breiten geworden. `hidden` ist exakt die
  // frueher Karte, `wide` exakt die fruehere Liste — die Enden bleiben also
  // erreichbar, dazwischen liegt neu die geteilte Ansicht.
  const [railWidth, setRailWidth] = useRailWidthPreference(feedId);

  /**
   * Task B5: unterhalb von 1024 px gibt es keine Teilung, sondern zwei Ebenen.
   *
   * Nebeneinander blieben dem Wort-Diff neben 300 px Leiste rund 580 px — und
   * der Diff ist genau das, was geprueft wird. Statt beides zu quetschen,
   * zeigt die Werkbank entweder die Liste ODER das Detail, mit einem
   * ausdruecklichen Rueckweg. `railWidth` bleibt davon unberuehrt: die Wahl
   * gehoert dem Nutzer und soll ein Drehen des Geraets ueberleben.
   */
  const isNarrow = useIsNarrowViewport();
  const [narrowLevel, setNarrowLevel] = useState<'rail' | 'detail'>('rail');

  /**
   * Die letzte Entscheidung DIESER SITZUNG, fuer das Rueckgaengig im
   * Kartenfuss (Spec 4.4).
   *
   * Sie muss gemerkt werden, weil Freigeben und Ablehnen sofort zum naechsten
   * Produkt weiterspringen: das "aktuelle" Produkt ist danach nicht mehr das
   * entschiedene. Bewusst nur EIN Schritt und nur diese Sitzung - ein tieferer
   * Stapel weckt die Erwartung, beliebig weit zurueckgehen zu koennen, und die
   * traegt kein Serverzustand.
   *
   * Die SAMMEL-Ruecknahme aus Spec 9.5 ist damit ausdruecklich NICHT gebaut:
   * sie braucht eine eigene RPC, weil `bulk_update_approval_status` auf
   * `pending_review` filtert und den Rueckweg von `approved` deshalb nicht
   * gehen kann. Der Einzelfall hier laeuft ueber `reopenProduct`, und das hat
   * diesen Filter nicht.
   */
  const [lastDecidedId, setLastDecidedId] = useState<string | null>(null);

  /**
   * Die gemerkte Entscheidung gehoert dem Feed, in dem sie gefallen ist.
   *
   * Der Feedwechsel laeuft ueber den globalen Umschalter im Layout und
   * montiert den Reviewer NICHT neu (SelectionContext setzt Konto, MC und
   * Feed in einem Render, der Weg A nach B fuehrt nie ueber null). Ohne
   * diesen Reset blieb "Rueckgaengig" nach dem Wechsel aktiv und haette in
   * den ALTEN Feed geschrieben, waehrend die Aufraeumarbeit den neuen trifft.
   */
  /**
   * Wieviele Produkte die LETZTE Sammelfreigabe beigesteuert hat.
   *
   * Nur zur Beschriftung des Leerzustands: nach einer Freigabe ueber 3.777
   * Produkte springt die Kopfzahl auf 4.189, und ohne diese Zahl ist nicht zu
   * sehen, woher die restlichen 412 kommen (Mockup C2Danach). Bewusst
   * fluechtig - "gerade" gilt fuer diese Sitzung, ein Neuladen laesst die
   * Zeile zu Recht verschwinden.
   */
  const [zuletztSammelFreigegeben, setZuletztSammelFreigegeben] = useState(0);

  useEffect(() => {
    setLastDecidedId(null);
    setZuletztSammelFreigegeben(0);
  }, [feedId]);
  // Der Reiter bestimmt die MENGE (offen / freigegeben / abgelehnt), die
  // Filterleiste darunter verfeinert sie. Pro Feed gemerkt, aus demselben
  // Grund wie die Ansichtswahl.
  const [status, setStatus] = useStatusPreference(feedId, readStatusFromSearch(searchParams));
  const { counts: statusCounts } = useWorkbenchStatusCounts(feedId);

  /**
   * URL gewinnt ueber localStorage (Spec 5.1) — und zwar NUR bei einem
   * GUELTIGEN Parameter. Fehlt er oder ist er unbekannt, bleibt die
   * bestehende Praeferenz unangetastet (`readStatusFromSearch` liefert dafuer
   * `null`, keinen Ruecksprung auf "offen").
   *
   * Bewusst OHNE `withSavedEdits`: der Effect reagiert auf eine Aenderung der
   * URL-Parameter, nicht auf einen Klick, und die Spec verlangt ausdruecklich,
   * dass er nicht gegen eine laufende Handkorrektur gesperrt wird ("Der
   * URL-Effekt laeuft nur bei Aenderung der Parameter, nicht gegen
   * ungespeicherte Aenderungen"). Der Klick-Pfad (Reiterwechsel, Pillen-Klick)
   * bleibt ueber `requestStatus`/`requestFilters` durch `withSavedEdits`
   * geschuetzt.
   *
   * `setStatus` (aus `useStatusPreference`) schreibt bei einem echten Wechsel
   * automatisch die Praeferenz mit — kein zweiter Schreibpfad noetig.
   */
  useEffect(() => {
    const urlStatus = readStatusFromSearch(searchParams);
    if (urlStatus && urlStatus !== status) {
      setStatus(urlStatus);
    } else if (urlStatus) {
      // Das Saeen hat den Reiter schon getroffen, die Praeferenz fehlt dann
      // noch: `setStatus` ist der einzige Ort, der sie sonst mitschreibt, und
      // er laeuft in diesem Zweig nicht. Ein erneutes Schreiben desselben
      // Werts ist folgenlos.
      writeStatusPreference(feedId, urlStatus);
    }

    // `scope=awaiting_upload` ist KEINE Praeferenz (kein localStorage) — er
    // gilt nur, solange der Parameter in der URL steht.
    const scopeActive = hasAwaitingUploadScope(searchParams);
    if (scopeActive !== filters.awaitingUploadOnly) {
      setFilters((prev) => ({ ...prev, awaitingUploadOnly: scopeActive }));
    }

    // WP5 A2O-02: `rule` ist wie `scope` keine Praeferenz.
    const urlRule = readRuleFromSearch(searchParams);
    if (urlRule !== (filters.ruleId ?? null)) {
      setFilters((prev) => ({ ...prev, ruleId: urlRule }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- laeuft bewusst nur bei Aenderung der URL-Parameter, nicht bei jeder Statusaenderung von innen (sonst ueberschriebe ein Reiterwechsel, der die URL selbst setzt, sich selbst in einer Schleife).
  }, [searchParams]);

  /**
   * Ein Deep-Link gilt fuer die NAVIGATION, nicht fuer jeden spaeteren Feed.
   *
   * `useStatusPreference` zieht den Reiter beim Feedwechsel WAEHREND des
   * Renders auf die Praeferenz des neuen Feeds nach; der Effect darueber hoert
   * nur auf die Suchparameter und laeuft dabei nicht mit. Nach "Chip →
   * /workbench?status=approved&scope=awaiting_upload → Feed in der Kopfleiste
   * wechseln" zeigte die Werkbank die Praeferenz des neuen Feeds, waehrend die
   * Adresse weiter `status=approved` behauptete.
   *
   * Geraeumt statt erneut angewendet: den Parameter erneut anzuwenden hiesse,
   * die gemerkte Reiterwahl des neuen Feeds still zu ueberschreiben
   * (`setStatus` schreibt die Praeferenz mit). Und `scope` ist ausdruecklich
   * keine Praeferenz -- eine andere Produktmenge zu filtern, nach der niemand
   * gefragt hat, waere die zweite Unwahrheit.
   */
  const letzterFeed = useRef<string | null>(feedId);
  useEffect(() => {
    if (letzterFeed.current === feedId) return;
    letzterFeed.current = feedId;
    if (!searchParams.has('status') && !searchParams.has('scope') && !searchParams.has(RULE_PARAM)) return;
    const naechste = new URLSearchParams(searchParams);
    naechste.delete('status');
    naechste.delete('scope');
    naechste.delete(RULE_PARAM);
    setSearchParams(naechste, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur der Feedwechsel loest das aus; `searchParams` in den Deps liesse das Raeumen bei jeder Parameter-Aenderung erneut pruefen, ohne dass sich der Feed bewegt hat.
  }, [feedId]);

  /* Erfolgsmoment-Schalter: der Feed-Durchschnitt aus dem juengsten Befund.
     Eigener Hook (mockbar wie useWorkbenchStatusCounts — dieses Bauteil wird
     BEWUSST ohne QueryClient getestet); das enabled-Gate haelt /workbench
     requestfrei. */
  const feedAvgScore = useErfolgsmomentAvgScore(
    feedId,
    leerzustand === 'erfolgsmoment' && approvedAwaitingUpload > 0,
  );

  const {
    products,
    isLoading,
    error,
    isPaused,
    refetch,
    currentProduct,
    currentIndex,
    total,
    sessionAccepted,
    sessionRejected,
    undecidedLoadedCount,
    mayHaveMore,
    currentDecision,
    currentWasEdited,
    isCurrentReviewed,
    getReviewDecision,
    wasEdited,
    acceptProduct,
    rejectProduct,
    reopenProduct,
    bulkApprove,
    bulkReject,
    approveScope,
    reopenBulk,
    saveChanges,
    goToPrev,
    goToNext,
    goToProduct,
    canGoPrev,
    canGoNext,
    bulkSummary,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    listLoadedCount,
    serverTotalUnknown,
    bulkSummaryFailed,
    refetchBulkSummary,
    listPendingTotal,
    autoLoadCapReached,
    unlockMoreAutoLoad,
  } = useWorkbench(feedId, filters, status);

  const [editState, setEditState] = useState<WorkbenchEditState>({
    optimized_title: '',
    optimized_description: '',
    optimized_product_highlights: [],
    optimized_color: '',
    optimized_material: '',
    optimized_gender: '',
    optimized_age_group: '',
    optimized_size: '',
    optimized_pattern: '',
    optimized_google_product_category: '',
  });
  const [originalEditState, setOriginalEditState] = useState<WorkbenchEditState>({
    optimized_title: '',
    optimized_description: '',
    optimized_product_highlights: [],
    optimized_color: '',
    optimized_material: '',
    optimized_gender: '',
    optimized_age_group: '',
    optimized_size: '',
    optimized_pattern: '',
    optimized_google_product_category: '',
  });
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Refs to access current edits from the auto-save effect
  const editStateRef = useRef(editState);
  const originalEditStateRef = useRef(originalEditState);
  const currentProductRef = useRef(currentProduct);
  editStateRef.current = editState;
  originalEditStateRef.current = originalEditState;
  currentProductRef.current = currentProduct;

  const getHasUnsavedChanges = useCallback(() => {
    const es = editStateRef.current;
    const oes = originalEditStateRef.current;
    return (
      es.optimized_title !== oes.optimized_title ||
      es.optimized_description !== oes.optimized_description ||
      es.optimized_color !== oes.optimized_color ||
      es.optimized_material !== oes.optimized_material ||
      es.optimized_gender !== oes.optimized_gender ||
      es.optimized_age_group !== oes.optimized_age_group ||
      es.optimized_size !== oes.optimized_size ||
      es.optimized_pattern !== oes.optimized_pattern ||
      es.optimized_google_product_category !== oes.optimized_google_product_category ||
      JSON.stringify(es.optimized_product_highlights) !== JSON.stringify(oes.optimized_product_highlights)
    );
  }, []);

  const hasUnsavedChanges =
    editState.optimized_title !== originalEditState.optimized_title ||
    editState.optimized_description !== originalEditState.optimized_description ||
    editState.optimized_color !== originalEditState.optimized_color ||
    editState.optimized_material !== originalEditState.optimized_material ||
    editState.optimized_gender !== originalEditState.optimized_gender ||
    editState.optimized_age_group !== originalEditState.optimized_age_group ||
    editState.optimized_size !== originalEditState.optimized_size ||
    editState.optimized_pattern !== originalEditState.optimized_pattern ||
    editState.optimized_google_product_category !== originalEditState.optimized_google_product_category ||
    JSON.stringify(editState.optimized_product_highlights) !==
      JSON.stringify(originalEditState.optimized_product_highlights);

  // Auto-save unsaved edits when navigating away from a product
  const prevProductIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevId = prevProductIdRef.current;
    const newId = currentProduct?.id ?? null;

    if (prevId && prevId !== newId && getHasUnsavedChanges()) {
      // Fire-and-forget: save the previous product's edits
      saveChanges(prevId, editStateRef.current);
    }

    prevProductIdRef.current = newId;
  }, [currentProduct?.id]);

  // Auto-save beim VERLASSEN der Werkbank (Unmount): der Effect oben feuert
  // nur beim Produktwechsel — wer stattdessen ueber Fortschrittsleiste oder
  // Seitenleiste navigierte, verlor eine begonnene Korrektur still, obwohl die
  // Karte sie bereits als "das, was hochgeladen wuerde" anzeigte. Bewusst ein
  // EIGENER Effect mit leerem Dep-Array (nicht als Cleanup am Effect oben,
  // der wuerde bei jedem Produktwechsel doppelt speichern). `saveChangesRef`,
  // weil saveChanges pro Render neu entsteht und die Cleanup-Closure sonst die
  // Fassung des ERSTEN Renders (mit veraltetem Query-Key/Pfad) festhielte.
  const saveChangesRef = useRef(saveChanges);
  saveChangesRef.current = saveChanges;
  useEffect(() => {
    return () => {
      const product = currentProductRef.current;
      if (product && getHasUnsavedChanges()) {
        // Fire-and-forget: der DB-Write ist das, was zaehlt — die Anzeige ist
        // in diesem Moment bereits ausgehaengt.
        saveChangesRef.current(product.id, editStateRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- laeuft genau einmal beim Unmount; alle Werte kommen aus stabilen Refs.
  }, []);

  /**
   * Tab schliessen oder neu laden — der einzige Weg, den kein Speichern
   * abfaengt.
   *
   * V2-Befund: die Werkbank hat keinen Speichern-Knopf; die einzige Persistenz
   * ist der Auto-Save beim Produktwechsel und der beim React-Unmount. Beim
   * Schliessen des Tabs laeuft KEINER von beiden — React raeumt dort nicht mehr
   * auf. Die Korrektur war damit ohne jeden Hinweis weg, obwohl die Karte sie
   * als "das, was hochgeladen wuerde" ausgewiesen hat.
   *
   * Bewusst nur eine WARNUNG und kein Rettungsversuch: ein Schreibvorgang im
   * `beforeunload` ist nicht zuverlaessig durchzubekommen (kein `await`,
   * Verbindungen werden abgeraeumt), und ein `sendBeacon` an PostgREST muesste
   * den Zugriffstoken mitgeben und die Antwort ignorieren — ein halb
   * geschriebenes Produkt waere schlimmer als ein bewusst verworfenes. Der
   * Nutzer entscheidet.
   *
   * Der Text stammt vom Browser, nicht von uns: seit Chrome 60 / Firefox 44
   * wird jede eigene Meldung verworfen. `preventDefault()` plus `returnValue`
   * deckt beide Auslegungen der Spezifikation ab.
   */
  useEffect(() => {
    const warnen = (e: BeforeUnloadEvent) => {
      if (!getHasUnsavedChanges()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [getHasUnsavedChanges]);

  /**
   * Dieselbe Bruecke fuer den Upload-Knopf der Seite.
   *
   * Er kann den Bearbeitungszustand nicht sehen und lud deshalb den alten Text
   * hoch. Gemeldet wird eine Funktion, kein Zustand: nur so bleibt die
   * Reihenfolge (schreiben, abwarten, dann hochladen) beim Aufrufer erzwingbar,
   * und ein Fehlschlag kann den Upload abbrechen statt ihn mit veraltetem Text
   * durchzulassen.
   */
  useEffect(() => {
    if (!registerFlushPendingEdits) return;
    registerFlushPendingEdits(async () => {
      const product = currentProductRef.current;
      if (!product || !getHasUnsavedChanges()) return true;
      try {
        // K2-Befund: derselbe Snapshot-Versatz wie in flushEdits (siehe dort).
        const snapshot = editStateRef.current;
        const gespeichert = await saveChangesRef.current(product.id, snapshot);
        if (gespeichert === false) return false;
        setOriginalEditState(snapshot);
        return true;
      } catch {
        // Wie ueberall auf diesem Pfad: der Toast aus saveChanges greift, und
        // ein Fehlschlag heisst NICHT hochladen.
        return false;
      }
    });
    return () => registerFlushPendingEdits(null);
  }, [registerFlushPendingEdits, getHasUnsavedChanges]);

  // Reset edit state when product changes
  useEffect(() => {
    if (currentProduct) {
      const initial = buildEditState(
        currentProduct.optimized_title,
        currentProduct.optimized_description,
        currentProduct.optimized_product_highlights,
        currentProduct.optimized_color,
        currentProduct.optimized_material,
        currentProduct.optimized_gender,
        currentProduct.optimized_age_group,
        currentProduct.optimized_size,
        currentProduct.optimized_pattern,
        currentProduct.optimized_google_product_category,
      );
      setEditState(initial);
      setOriginalEditState(initial);
    }
  }, [currentProduct?.id]);

  const handleEditChange = useCallback(
    (field: keyof WorkbenchEditState, value: string | string[]) => {
      setEditState((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Bericht 2.6: der window-Keydown-Listener umgeht das disabled-Attribut der
  // Buttons — Enter/Leertaste konnten waehrend eines laufenden Accepts/Rejects
  // einen zweiten Request ausloesen. Ein Ref statt (nur) State, weil der
  // State-Guard erst nach dem naechsten Render greift und gehaltene bzw.
  // schnell wiederholte Tasten genau in dieses Fenster fallen.
  const reviewActionInFlight = useRef(false);

  const handleAccept = async () => {
    if (!currentProduct || isCurrentReviewed || reviewActionInFlight.current) return;
    reviewActionInFlight.current = true;
    setIsAccepting(true);
    try {
      // Auto-save edits before accepting — abort on save failure so the
      // unreviewed edits are never silently discarded by an approval (F-2).
      if (hasUnsavedChanges) {
        const saved = await saveChanges(currentProduct.id, editState);
        if (!saved) return;
        setOriginalEditState(editState);
      }
      await acceptProduct(currentProduct.id);
      setLastDecidedId(currentProduct.id);
    } finally {
      reviewActionInFlight.current = false;
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!currentProduct || isCurrentReviewed || reviewActionInFlight.current) return;
    reviewActionInFlight.current = true;
    setIsRejecting(true);
    try {
      // Auto-save edits before rejecting (so they're not lost) — abort on
      // save failure so rejection never runs against an unsaved edit (F-2).
      if (hasUnsavedChanges) {
        const saved = await saveChanges(currentProduct.id, editState);
        if (!saved) return;
        setOriginalEditState(editState);
      }
      await rejectProduct(currentProduct.id);
      setLastDecidedId(currentProduct.id);
    } finally {
      reviewActionInFlight.current = false;
      setIsRejecting(false);
    }
  };

  /**
   * "Bearbeiten" aus der Aktionsleiste (Reiter "Freigegeben" / "Abgelehnt").
   *
   * Der Bearbeiten-Zustand ist bewusst NICHT hierher hochgezogen: er lebt pro
   * FELD in `WorkbenchProductCard` (jede der drei Zeilen klappt einzeln auf),
   * und ein gemeinsamer Schalter waere eine andere Bedienung als die, die im
   * Reiter "Offen" seit Langem funktioniert. Die Leiste oeffnet deshalb genau
   * das, was ein Mausklick auch oeffnen wuerde: die erste aufklappbare Zeile
   * der Karte. Die Naht ist das `data-testid`, das die Karte ohnehin fuer
   * Tests und aria vergibt.
   */
  const handleEditFromActionBar = useCallback(() => {
    const openTrigger = () => {
      const trigger = document.querySelector<HTMLButtonElement>('[data-testid^="workbench-edit-"]');
      trigger?.focus();
      trigger?.click();
    };
    const mounted = document.querySelector('[data-testid^="workbench-edit-"]');
    if (mounted) {
      openTrigger();
      return;
    }
    // K1-Befund: auf der schmalen Leisten-Ebene ist die Produktkarte nicht
    // montiert — der querySelector fand nichts, und "Bearbeiten" war ein
    // stiller No-op (der teuerste UI-Fehler: ein Knopf, der nichts tut).
    // Erst auf die Detail-Ebene wechseln, dann nach dem Commit den ersten
    // Anpassen-Schalter oeffnen. Doppel-rAF, weil der erste Frame vor dem
    // React-Commit feuern kann; der document-Guard schuetzt vor einem
    // Teardown zwischen Klick und Frame (Lehre aus dem #166-Hotfix).
    setNarrowLevel('detail');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof document === 'undefined') return;
        openTrigger();
      });
    });
  }, []);


  /** Sammelauswahl der Leiste. Lebt hier, weil die Leiste rein darstellend ist. */
  const [railSelectedIds, setRailSelectedIds] = useState<Set<string>>(new Set());
  const toggleRailSelection = useCallback((id: string) => {
    setRailSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * Auswahlmodus der schmalen Leiste (Spec B.5).
   *
   * Ausschalten raeumt die Auswahl mit ab. Sonst bliebe eine unsichtbare
   * Auswahl stehen, die beim naechsten Einschalten wieder auftaucht — der
   * Nutzer haette Haken gesetzt, die er nicht mehr sieht.
   */
  const [railSelectionMode, setRailSelectionMode] = useState(false);
  const setRailSelectionModeAndClear = useCallback((on: boolean) => {
    setRailSelectionMode(on);
    if (!on) setRailSelectedIds(new Set());
  }, []);

  /**
   * Sammelhandlungen der Leiste. Die Leiste reicht ausschliesslich geladene,
   * unentschiedene IDs herein (siehe `validSelectedIds` dort); hier wird nur
   * noch ausgefuehrt und die Auswahl geleert — eine Auswahl, die eine
   * Sammelhandlung ueberlebt, laedt zum zweiten Klick auf dieselbe Menge ein.
   */

  /**
   * Cursorwechsel aus der Leiste — der riskanteste Pfad dieses Umbaus.
   *
   * Bis Task B4 wechselte das aktive Produkt nur per Tastendruck, also
   * serialisiert. Mit der Leiste kann es sich per MAUSKLICK aendern, waehrend
   * ein Textfeld den Fokus haelt und ungespeicherte Aenderungen traegt.
   * Diesen Codepfad gab es vorher nicht, und sein Fehlerbild ist die still
   * verlorene Korrektur — das teuerste Ergebnis, das dieser Bildschirm
   * produzieren kann.
   *
   * Die Reihenfolge ist deshalb nicht verhandelbar:
   *   1. Speichern anstossen
   *   2. ABWARTEN
   *   3. erst dann den Cursor umsetzen
   *
   * Ein Klick waehrend eines laufenden Speicherns wird EINGEREIHT, nicht
   * verworfen: `pendingCursorRef` haelt das zuletzt gewuenschte Ziel, und nach
   * Abschluss wird genau dieses angesprungen. Ein verworfener Klick waere fuer
   * den Nutzer ein toter Bildschirm.
   *
   * Schlaegt das Speichern fehl, bleibt der Cursor stehen. Sonst waere die
   * Korrektur weg UND der Nutzer stuende woanders — dasselbe Muster wie der
   * Abbruch bei Accept/Reject.
   */
  /**
   * K1-Befund: der Speicher-Kern des Tors, herausgeloest, damit auch die
   * HANDLUNGS-Pfade (Reopen, Bulk) ihn abwarten koennen. Gibt zurueck, ob es
   * sicher ist weiterzumachen: true = gespeichert oder nichts zu speichern,
   * false = Fehlschlag (der Toast aus saveChanges greift, der Aufrufer tut
   * NICHTS — sonst ruft resetBuffer den Entwurf ab, bevor er je die DB sah).
   */
  const flushEdits = useCallback(async (): Promise<boolean> => {
    if (!currentProduct || !getHasUnsavedChanges()) return true;
    try {
      // K2-Befund: EIN Snapshot fuer Payload UND Markierung. Vorher ging der
      // Stand VOR dem await in die DB (S1), als gespeichert markiert wurde
      // aber der Ref-Stand NACH dem await (S2) — Tastatureingaben waehrend
      // des Roundtrips waren damit nie persistiert und nie wieder als
      // ungespeichert erkennbar. Mit dem Snapshot bleibt S2 != S1 sichtbar
      // und der naechste Flush schreibt die fehlenden Zeichen nach.
      const snapshot = editStateRef.current;
      const saved = await saveChanges(currentProduct.id, snapshot);
      if (saved === false) return false;
      setOriginalEditState(snapshot);
      return true;
    } catch {
      return false;
    }
  }, [currentProduct, getHasUnsavedChanges, saveChanges]);

  /**
   * K1-Befund (L3/L6): der Feedwechsel im globalen FeedSwitcher war der
   * einzige im V2-Original-Befund genannte Bewegungspfad OHNE Tor. Er liegt
   * ausserhalb dieses Baums (Layout), deshalb geht der Speicher-Kern ueber
   * das Modul-Register dorthin: der FeedSwitcher fragt vor jedem Wechsel nach
   * und wechselt bei Fehlschlag nicht. Der Ref haelt die je aktuelle
   * flushEdits-Fassung, damit die Registrierung nicht pro Render wandert.
   */
  const flushEditsRef = useRef(flushEdits);
  useEffect(() => {
    flushEditsRef.current = flushEdits;
  }, [flushEdits]);
  useEffect(() => registerPendingEditsGuard(() => flushEditsRef.current()), []);

  /**
   * K5-Befund: nach einer Sammel-Entscheidung fiel der Tastaturfokus auf
   * <body> — der fokussierte Knopf unmountete (Leisten-Sammelleiste,
   * Kandidaten-Knopf nach geleerter Liste) oder wurde disabled
   * (Listenansicht). Der aktive Reiter ist das stabilste fokussierbare
   * Element dieser Flaeche (immer montiert, traegt aria-current-Kontext) —
   * dorthin. rAF + document-Guard nach dem Muster aus Nr. 57.
   */
  const focusActiveTab = useCallback(() => {
    requestAnimationFrame(() => {
      if (typeof document === 'undefined') return;
      document.getElementById(`workbench-tab-${status}`)?.focus();
    });
  }, [status]);

  const pendingMoveRef = useRef<(() => void) | null>(null);
  const cursorSaveInFlightRef = useRef(false);

  /**
   * Das EINE Tor, durch das jede Bewegung geht, die den Entwurf gefaehrdet.
   *
   * V2-Befund: die Reihenfolge war an genau EINER von sechs Stellen gebaut —
   * dem Zeilenklick. Weiter/Zurueck-Knopf, Pfeiltasten, Reiter-, Filter- und
   * Suchwechsel versetzten den Cursor sofort; der Auto-Save lief danach als
   * fire-and-forget, ohne `await` und ohne den Rueckgabewert anzusehen. Bei
   * einem Netzabbruch stand der Nutzer beim naechsten Produkt, sah den Toast
   * "Speichern fehlgeschlagen" — und sein Text war weg, weil der
   * Geschwister-Effekt `editState` inzwischen mit den Werten des neuen
   * Produkts ueberschrieben hat. Es gibt keinen Zwischenspeicher, kein
   * Speichern pro Feld und keinen Wiederholen-Knopf.
   *
   * Deshalb nimmt das Tor eine BEWEGUNG entgegen, nicht eine Produkt-ID: den
   * Reiterwechsel kann man nicht als Cursorziel ausdruecken, und er ist genau
   * so gefaehrlich.
   */
  const withSavedEdits = useCallback(
    async (move: () => void) => {
      pendingMoveRef.current = move;

      // Laeuft schon ein Speichern, uebernimmt dessen Abschluss die Bewegung.
      // Einreihen statt verwerfen: ein verworfener Klick ist fuer den Nutzer
      // ein toter Bildschirm.
      if (cursorSaveInFlightRef.current) return;

      if (!currentProduct || !getHasUnsavedChanges()) {
        pendingMoveRef.current = null;
        move();
        return;
      }

      cursorSaveInFlightRef.current = true;
      try {
        // Fehlschlag: NICHT bewegen. Sonst waere die Korrektur weg UND der
        // Nutzer stuende woanders.
        if (!(await flushEdits())) return;
        const ausstehend = pendingMoveRef.current;
        pendingMoveRef.current = null;
        ausstehend?.();
      } finally {
        cursorSaveInFlightRef.current = false;
      }
    },
    [currentProduct, getHasUnsavedChanges, flushEdits],
  );

  /**
   * K1-Befund: reopenProduct und die Bulk-Pfade riefen `resetBuffer()` im
   * Hook, ohne dass der offene Entwurf vorher gespeichert war — dieselbe
   * Fehlerklasse, die das Tor fuer die sechs Bewegungspfade geschlossen hat,
   * nur ueber die Handlungs-Seite. Deshalb warten sie jetzt ALLE `flushEdits`
   * ab: bei Fehlschlag passiert nichts, und die Leisten-Auswahl bleibt
   * stehen, damit der zweite Versuch dieselbe Menge trifft. Die Zaehler
   * werden echt durchgereicht (die Listen-Konsumenten zeigen sie an).
   */
  const handleReopen = useCallback(() => {
    if (!currentProduct) return;
    const id = currentProduct.id;
    void (async () => {
      if (!(await flushEdits())) return;
      void reopenProduct(id);
    })();
  }, [currentProduct, flushEdits, reopenProduct]);

  const guardedBulkApprove = useCallback(
    async (ids: string[]): Promise<number> => {
      if (!(await flushEdits())) return 0;
      const count = await bulkApprove(ids);
      if (count > 0) focusActiveTab();
      return count;
    },
    [bulkApprove, flushEdits, focusActiveTab],
  );
  const guardedBulkReject = useCallback(
    async (ids: string[]): Promise<number> => {
      if (!(await flushEdits())) return 0;
      const count = await bulkReject(ids);
      if (count > 0) focusActiveTab();
      return count;
    },
    [bulkReject, flushEdits, focusActiveTab],
  );
  const handleRailBulkApprove = useCallback(
    (ids: string[]) => {
      void (async () => {
        if (!(await flushEdits())) return;
        // K2-Befund: bulkApprove wirft nie (faengt intern, Toast + return 0).
        // Die Leerung haengt deshalb am Zaehler — schlaegt die RPC fehl,
        // bleibt die Auswahl fuer den zweiten Versuch stehen (dasselbe
        // Muster wie in der Listenansicht).
        const count = await bulkApprove(ids);
        if (count > 0) {
          setRailSelectedIds(new Set());
          focusActiveTab();
        }
      })();
    },
    [bulkApprove, flushEdits, focusActiveTab],
  );
  const handleRailBulkReject = useCallback(
    (ids: string[]) => {
      void (async () => {
        if (!(await flushEdits())) return;
        const count = await bulkReject(ids);
        if (count > 0) {
          setRailSelectedIds(new Set());
          focusActiveTab();
        }
      })();
    },
    [bulkReject, flushEdits, focusActiveTab],
  );

  const requestCursor = useCallback(
    async (id: string) => {
      if (!currentProduct) return;

      /**
       * Dieselbe Zeile noch einmal: kein Cursorwechsel, aber sehr wohl ein
       * Ebenenwechsel.
       *
       * V1-Befund: auf dem Telefon steht der Cursor beim Aufbau auf
       * `products[0]` — ausgerechnet die erste Zeile der Liste war damit die
       * einzige, die auf einen Tipp nicht reagierte, waehrend alle anderen es
       * taten. Denselben Zustand erreicht man alltaeglich ueber "Zurueck zur
       * Liste" und erneutes Antippen derselben Zeile. Der Nutzer sieht der
       * Zeile nicht an, dass sie tot ist.
       *
       * Breit ist die Zuweisung folgenlos: `narrowLevel` wird nur gelesen,
       * wenn `isNarrow` gilt.
       */
      if (id === currentProduct.id) {
        setNarrowLevel('detail');
        return;
      }

      await withSavedEdits(() => {
        goToProduct(id);
        setNarrowLevel('detail');
      });
    },
    [currentProduct, goToProduct, withSavedEdits],
  );

  /**
   * Rueckgaengig fuer die letzte Entscheidung dieser Sitzung.
   *
   * Springt danach auf das betroffene Produkt zurueck: ohne den Sprung stuende
   * der Cursor irgendwo weiter hinten, und der Nutzer muesste die
   * zurueckgenommene Entscheidung suchen.
   */
  const handleUndoLast = useCallback(() => {
    if (!lastDecidedId) return;
    const id = lastDecidedId;
    void (async () => {
      if (!(await flushEdits())) return;
      const ok = await reopenProduct(id);
      // Erst nach dem gelungenen Rueckweg vergessen. Vorher stand das
      // `setLastDecidedId(null)` VOR dem await: scheiterte `flushEdits` oder
      // `reopenProduct`, war der Knopf danach gesperrt, obwohl die
      // Entscheidung noch stand - und im Reiter "Offen" gibt es keinen
      // zweiten Weg dorthin, weil der Cursor laengst weitergesprungen ist.
      if (!ok) return;
      setLastDecidedId(null);
      void requestCursor(id);
    })();
  }, [lastDecidedId, flushEdits, reopenProduct, requestCursor]);

  /** Blaettern — derselbe Schutz wie der Zeilenklick. */
  const requestNext = useCallback(() => {
    void withSavedEdits(goToNext);
  }, [withSavedEdits, goToNext]);

  const requestPrev = useCallback(() => {
    void withSavedEdits(goToPrev);
  }, [withSavedEdits, goToPrev]);

  /**
   * Reiter- und Filterwechsel wechseln die Produktmenge und damit das aktuelle
   * Produkt — fuer den offenen Entwurf ist das dasselbe Risiko wie ein
   * Cursorwechsel, nur ohne Cursorziel.
   *
   * Beide schreiben zusaetzlich die URL fort (Spec 5.1/5.2): ein
   * Reiterwechsel aktualisiert `status` und behaelt `scope`, ein Filterwechsel
   * setzt bzw. entfernt `scope`, sobald `awaitingUploadOnly` kippt. Die
   * URL-Schreibung steht INNERHALB der `withSavedEdits`-Bewegung, damit sie nur
   * dann passiert, wenn die Aenderung wirklich vollzogen wird (nicht bei einem
   * fehlgeschlagenen Speichern der offenen Handkorrektur).
   */
  const requestStatus = useCallback(
    (next: WorkbenchStatus) => {
      void withSavedEdits(() => {
        setStatus(next);
        setSearchParams(
          (prev) => {
            const params = new URLSearchParams(prev);
            params.set('status', next);
            return params;
          },
          { replace: true },
        );
      });
    },
    [withSavedEdits, setStatus, setSearchParams],
  );

  const requestFilters = useCallback(
    (next: WorkbenchFilters) => {
      void withSavedEdits(() => {
        setFilters(next);
        const scopeChanged = next.awaitingUploadOnly !== filters.awaitingUploadOnly;
        const ruleChanged = (next.ruleId ?? null) !== (filters.ruleId ?? null);
        // EIN setSearchParams-Aufruf fuer beide Parameter: zwei Aufrufe im
        // selben Tick lesen beide dieselbe alte Adresse, der zweite
        // ueberschriebe den ersten.
        if (scopeChanged || ruleChanged) {
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              if (scopeChanged) {
                if (next.awaitingUploadOnly) params.set('scope', AWAITING_UPLOAD_SCOPE);
                else params.delete('scope');
              }
              if (ruleChanged) {
                if (next.ruleId) params.set(RULE_PARAM, next.ruleId);
                else params.delete(RULE_PARAM);
              }
              return params;
            },
            { replace: true },
          );
        }
      });
    },
    [withSavedEdits, filters.awaitingUploadOnly, filters.ruleId, setSearchParams],
  );

  /* --- Sammelfreigabe (Etappe 4, Spec 9) ------------------------------- */

  /** Laeuft gerade eine Sammelfreigabe? Sperrt Menue und Rueckfrage. */
  const [isBulkReleasing, setIsBulkReleasing] = useState(false);

  /**
   * Menueeintrag 1 und 3 (Spec 9.2/9.3), samt Rueckgaengig aus Spec 9.5.
   *
   * `flushEdits` steht zwingend am Anfang und nicht als Nebensache: der Hook
   * ruft in `approveScope` ein `resetBuffer()`, und das verwirft eine offene
   * Handkorrektur still. Genau dieselbe Reihenfolge wie in
   * `guardedBulkApprove` - der Unterschied ist nur, dass die Menge hier
   * serverseitig bestimmt wird und deshalb keine ID-Liste hereinkommt.
   */
  const handleApproveScope = useCallback(
    async (scope: BulkScope): Promise<number> => {
      if (!(await flushEdits())) return 0;

      setIsBulkReleasing(true);
      let approved = 0;
      let ids: string[] = [];
      try {
        const ergebnis = await approveScope(scope);
        approved = ergebnis.approved;
        ids = ergebnis.ids;
      } finally {
        setIsBulkReleasing(false);
      }

      // 0 heisst: es ist nichts passiert. Kein Erfolgs-Toast - entweder war
      // die Menge leer, oder der Schreibvorgang ist gescheitert und hat im
      // Hook bereits seinen eigenen Fehler gemeldet. Ein "0 Produkte
      // freigegeben" obendrauf laese sich wie ein Erfolg.
      if (approved === 0) return 0;

      setZuletztSammelFreigegeben(approved);
      focusActiveTab();
      /**
       * Doppelklick-Riegel fuer "Rueckgaengig" - bewusst eine Closure-Variable
       * DIESES Aufrufs und kein `useRef` der Komponente.
       *
       * Ein Ref waere ein Riegel fuer ALLE Meldungen zusammen, und es steht
       * regelmaessig mehr als eine: sonner zeigt bis zu drei Meldungen
       * gleichzeitig (`visibleToasts`, von `App.tsx` nicht ueberschrieben) und
       * das Fenster hier ist 12 Sekunden lang. Wer in zwei Durchgaengen
       * freigibt und dann beide Rueckwege klickt, liess mit einem geteilten
       * Riegel den zweiten Klick ins Leere laufen: kein RPC, keine Meldung,
       * keine Aenderung - waehrend sonner die Meldung trotzdem schliesst und
       * es damit wie ein Erfolg aussieht. Diese Produkte blieben freigegeben
       * und gingen beim naechsten Upload zu Google.
       *
       * Eine Variable je Meldung schuetzt weiterhin gegen den Doppelklick auf
       * DIESELBE Meldung, und genau darum ging es dem Riegel.
       */
      let undoLaeuft = false;
      toast({
        tone: 'success',
        title: tRef.current('release.doneToast', { count: approved }),
        action: {
          label: tRef.current('release.undo'),
          onClick: () => {
            if (undoLaeuft) return;
            undoLaeuft = true;
            void (async () => {
              const { zurueck, vollstaendig } = await reopenBulk(ids);

              // Genau EINE Meldung, und sie nennt den Ausgang beim Namen.
              // Vorher konnte ein Teilausfall gleichzeitig die Fehlermeldung
              // aus dem Hook und die Erfolgsmeldung von hier zeigen, weil
              // `zurueck` dann eine echte Teilsumme trug - und der Riegel
              // blieb zu, obwohl der Rest noch freigegeben war.
              if (vollstaendig && zurueck > 0) {
                setZuletztSammelFreigegeben(0);
                toast({ tone: 'success', title: tRef.current('release.undoneToast', { count: zurueck }) });
                // Nur im Erfolg zugelassen bleiben: danach gibt es nichts
                // mehr zurueckzunehmen, ein zweiter Klick traefe eine Menge,
                // die schon wieder offen ist.
                return;
              }

              // Teilweise oder gar nicht: der Riegel geht wieder auf, damit
              // der Rest ueberhaupt noch erreichbar ist.
              undoLaeuft = false;
              toast({
                title: zurueck > 0
                  ? tRef.current('release.undoPartial', { zurueck, gesamt: ids.length })
                  : tRef.current('release.undoFailed'),
                variant: 'destructive',
              });
            })();
          },
        },
        // 12 Sekunden statt der Sonner-Vorgabe von rund 4 (Spec 9.5): das
        // Ergebnis einer Sammelfreigabe ist erst zu sehen, nachdem die Liste
        // nachgeladen hat. Wer in vier Sekunden entscheiden muss, hat es noch
        // gar nicht gesehen.
        durationMs: 12000,
      });
      return approved;
    },
    [flushEdits, approveScope, focusActiveTab, reopenBulk, toast],
  );

  /**
   * Menueeintrag 2: nur filtern, nichts freigeben.
   *
   * Setzt jetzt die Dimension, die der Eintrag verspricht:
   * `warningsOnly` filtert auf `products.validation_warnings` — dieselbe
   * Quelle, aus der `workbench_bulk_summary` die Zahl am Eintrag zaehlt.
   *
   * Vorher stand hier `scoreFilter: 'warning'`, also das Punkte-Band 40 bis
   * 69. Das ist eine ANDERE Menge: der Eintrag versprach N beanstandete
   * Produkte und zeigte danach eine nach Punkten gefilterte Liste, die sich
   * auf den gemessenen Feeds kaum mit ihr ueberschneidet.
   *
   * `scoreFilter: 'all'` wird bewusst mitgesetzt: der Eintrag heisst "zuerst
   * ansehen" und meint alle beanstandeten Produkte. Bliebe ein zuvor
   * gesetztes Punkte-Band stehen, versteckte es einen Teil davon, ohne dass
   * der Eintrag das ankuendigt. Die Suche bleibt dagegen erhalten — sie
   * definiert den Bereich, ueber den auch die Menuezahl gerechnet wurde.
   *
   * Ueber `requestFilters` und nicht ueber `setFilters`: ein Filterwechsel
   * tauscht das aktuelle Produkt aus, eine offene Handkorrektur muss vorher
   * geschrieben sein.
   *
   * Reichweite bleibt zu benennen: die Menuezahl ist feed-weit, der Filter
   * arbeitet auf den geladenen Zeilen (Begruendung in
   * `applyScoreFilterAndSort`). Die Trefferzahl danach kann also kleiner sein
   * als die Zahl im Menue, bis genug Seiten geladen sind.
   */
  const showWarningsOnly = useCallback(() => {
    requestFilters({ ...filters, warningsOnly: true, scoreFilter: 'all' });
  }, [requestFilters, filters]);

  /**
   * Menueeintrag 4: Auswahlmodus der Leiste einschalten.
   *
   * Bei Breite `hidden` rendert die Leiste ueberhaupt nicht (siehe `showRail`
   * unten). Ohne den Breitenwechsel schaltete der Eintrag also einen Modus
   * ein, dessen Flaeche es gar nicht gibt - ein Knopf, der sichtbar nichts
   * tut. `normal` ist die kleinste Breite, in der die Leiste steht.
   *
   * Bei `wide` wird die Breite bewusst NICHT angefasst: dort steht statt der
   * Leiste die Listenansicht, und die traegt ihre Auswahlspalte samt
   * Alles-auswaehlen ohnehin dauerhaft. Der Nutzer kann dort also schon
   * auswaehlen; ihn aus der Ansicht zu werfen, die er selbst gewaehlt hat,
   * waere teurer als der Modus, der dort nichts zu schalten hat.
   *
   * Die Breite allein reicht schmal nicht: unter 1024 px steht bei Breite
   * `normal` ENTWEDER die Leiste ODER das Detail (`showRail` unten verlangt
   * zusaetzlich `narrowLevel === 'rail'`). Wer auf dem Telefon gerade ein
   * Produkt ansieht, schaltete sonst einen Auswahlmodus ein, dessen Flaeche
   * nicht montiert ist - derselbe sichtbar wirkungslose Knopf, gegen den der
   * Breitenwechsel oben schon einmal gesetzt wurde, nur eine Ebene tiefer.
   * Bei `wide` bleibt auch die Ebene unangetastet: dort rendert die
   * Listenansicht statt der geteilten Ansicht, `narrowLevel` wird gar nicht
   * gelesen.
   */
  const enableSelection = useCallback(() => {
    if (railWidth === 'hidden') setRailWidth('normal');
    if (isNarrow && railWidth !== 'wide') setNarrowLevel('rail');
    setRailSelectionModeAndClear(true);
  }, [railWidth, setRailWidth, isNarrow, setRailSelectionModeAndClear]);

  /**
   * Das Buendel fuer die Kopfzeile der Seite.
   *
   * `null` in den Reitern "Freigegeben" und "Abgelehnt": dort ist jedes
   * Produkt bereits entschieden, ein "Freigeben"-Knopf haette keine Menge.
   * Die Identitaet muss stabil sein, weil die Anmeldung unten an ihr haengt -
   * jede Zutat ist deshalb entweder ein Wert oder eine useCallback.
   */
  const bulkReleaseHandle = useMemo<BulkReleaseHandle | null>(() => {
    if (status !== 'pending') return null;
    return {
      summary: bulkSummary,
      summaryFailed: bulkSummaryFailed,
      retrySummary: refetchBulkSummary,
      isBusy: isBulkReleasing,
      hasActiveSearch: filters.search.trim().length > 0,
      approveScope: handleApproveScope,
      showWarningsOnly,
      enableSelection,
    };
  }, [
    status,
    bulkSummary,
    bulkSummaryFailed,
    refetchBulkSummary,
    isBulkReleasing,
    filters.search,
    handleApproveScope,
    showWarningsOnly,
    enableSelection,
  ]);

  /**
   * Anmeldung bei der Seite. Abmeldung im Cleanup, damit die Kopfzeile beim
   * Verlassen der Werkbank keinen Knopf behaelt, hinter dem nichts mehr steht.
   *
   * Kein Kreisverkehr, obwohl die Anmeldung Seiten-State setzt: die Seite
   * reicht einen `useCallback([])`-Registrar herein, und die Identitaet des
   * Buendels haengt nur an eigenen Werten dieses Baums.
   */
  useEffect(() => {
    if (!registerBulkRelease) return;
    registerBulkRelease(bulkReleaseHandle);
    return () => registerBulkRelease(null);
  }, [registerBulkRelease, bulkReleaseHandle]);

  // Keyboard shortcuts — die Pfeiltasten blaettern jetzt direkt durch die
  // Produkte. Vorher liefen sie erst durch die beiden Reiter der Karte; die
  // gibt es nicht mehr, Texte und Attribute stehen gleichzeitig nebeneinander.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Die Kurzbefehle gehoeren zur Kartenansicht: nur sie zeigt ein
      // aktuelles Produkt und beschriftet die Tasten (WorkbenchActionBar).
      // In der Listenansicht traefen Enter/Leertaste den unsichtbaren
      // Index-Zeiger des Kartenpuffers — standardmaessig das BESTbewertete
      // offene Produkt — und jede weitere Taste das naechste (advanceToNext
      // laeuft auch im Listenpfad). Abgelehnt ist unumkehrbar teuer.
      if (railWidth === 'wide') return;

      // Task A7: Enter und Leertaste gehoeren dem Reiter "Offen". Wer aus
      // dem Schnelldurchlauf kommt, hat sie in den Fingern — im Reiter
      // "Freigegeben" wuerde derselbe Reflex ein bereits ausgeliefertes
      // Produkt anfassen. Die Blaetter-Pfeile bleiben ueberall belegt.
      const keyboardActions = actionSetFor(status).keyboardEnabled;
      if (!keyboardActions && (e.key === 'Enter' || e.key === ' ')) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // Ein offener modaler Dialog ist eine eigene Tastaturwelt. Dieser Handler
      // haengt am `window` und lief bisher auch hinter dem Dialog weiter: hinter
      // dem Bestaetigungsdialog wurde weitergeblaettert, und `preventDefault`
      // nahm dem Dialog seine eigenen Tasten weg. Mit dem Upload-Dialog ist das
      // kein Randfall mehr — seine Auswahlgruppe (RadioGroup) wird mit den
      // Pfeiltasten bedient. Der Sammelfreigabe-Dialog war schon vorher
      // betroffen. `data-state="open"` ist Pflicht: Radix laesst den Knoten
      // geschlossen im DOM stehen, eine Pruefung nur auf die Rolle machte die
      // Shortcuts nach dem ersten Dialog dauerhaft tot.
      //
      // `role="menu"` gehoert dazu, seit die Sammelfreigabe (Etappe 4) in
      // einem Radix-Aufklappmenue sitzt: ein Menue ist KEIN Dialog, die beiden
      // Rollen oben sehen es also nicht. Bei geoeffnetem Menue haetten Enter
      // und Leertaste weiterhin das Produkt DAHINTER freigegeben oder
      // abgelehnt - waehrend der Nutzer glaubt, einen Menueeintrag zu waehlen.
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"]',
        )
      ) {
        return;
      }

      // F1: Ein fokussierter Schalter hat seine EIGENE Tastaturbedeutung —
      // Enter und Leertaste betaetigen ihn. Ohne diese Ausnahme fingen die
      // globalen Shortcuts das ab (inklusive preventDefault) und der einzige
      // Tastaturweg in den Bearbeiten-Modus gab stattdessen das Produkt frei
      // beziehungsweise lehnte es ab. Seit die Karte "Anpassen"- und
      // "Fertig"-Schalter traegt, ist das kein Randfall mehr.
      //
      // N1: die Ausnahme gilt NUR fuer diese beiden Tasten. Pfeiltasten haben
      // auf einem Schalter keine eigene Bedeutung — sie pauschal mit zu
      // sperren machte die Blaetter-Navigation nach jedem Mausklick auf einen
      // Schalter tot, bis der Nutzer woandershin klickt.
      const onControl = target?.closest?.('button,[role="button"],a,[contenteditable]');
      if (onControl && (e.key === 'Enter' || e.key === ' ')) return;

      // V3-Befunde 5 und 8: zwei Flaechen haben ihre EIGENE Tastenbelegung
      // und liegen trotzdem im Fenster-Scope dieses Handlers.
      //
      // (a) Radix-Portale (FeedSwitcher-Popover, Menues) haengen an
      //     <body> — der Dialog-Guard oben sieht sie nicht, und ihre Optionen
      //     sind divs mit role="option", die der onControl-Guard nicht
      //     erfasst. Wer dort mit Enter einen Feed auswaehlte, gab mit
      //     demselben Druck das gerade angezeigte Produkt frei. Der Wrapper
      //     ist das verlaessliche Merkmal: unser eigenes Leisten-Listbox
      //     traegt ihn nicht, dessen Zeilen duerfen die globalen Tasten
      //     also weiterhin erreichen.
      if (target?.closest?.('[data-radix-popper-content-wrapper]')) return;
      // (b) Auf den Statusreitern bewegen die Pfeile den Reiter (Roving
      //     Tabindex) — derselbe Druck blaetterte hier zusaetzlich das
      //     Produkt weiter: EIN Tastendruck, ZWEI Handlungen.
      if (
        (e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
        target?.closest?.('[role="tablist"],[role="combobox"]')
      ) {
        return;
      }

      // Task B2 / Spec B.4: `[` klappt die Leiste ein und wieder aus. Haengt
      // bewusst in DIESEM Handler und nicht in einem eigenen: die
      // Schutzmechanismen oben (kein Auslösen im Textfeld, keins hinter einem
      // offenen Dialog, keins auf einem fokussierten Schalter) gelten damit
      // unveraendert mit. Ein zweiter Listener muesste sie kopieren, und die
      // Kopie liefe beim naechsten Umbau auseinander.
      if (e.key === '[') {
        e.preventDefault();
        setRailWidth(railWidth === 'hidden' ? 'normal' : 'hidden');
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (canGoNext) requestNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (canGoPrev) requestPrev();
      } else if (e.key === 'Enter' && !isCurrentReviewed) {
        e.preventDefault();
        handleAccept();
      } else if (e.key === ' ' && !isCurrentReviewed) {
        e.preventDefault();
        handleReject();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [railWidth, setRailWidth, status, canGoPrev, canGoNext, goToPrev, goToNext, handleAccept, handleReject, isCurrentReviewed]);

  /**
   * Leerzustaende behalten die Reiter.
   *
   * Im Browser-Lauf zu Teil A aufgefallen und der teuerste Fehler dieses
   * Umbaus gewesen: die Reiter standen unterhalb der Leerzustands-Ausstiege,
   * also war auf einem vollstaendig freigegebenen Feed KEIN Reiter sichtbar.
   * Genau dort wird "Freigegeben" gebraucht — der Nutzer sass vor
   * "Alles freigegeben" und hatte keinen Weg zu den Produkten, um die es
   * geht. Ein Leerzustand ist eine Aussage ueber DIESEN Reiter, nie ueber
   * die Werkbank.
   */
  /**
   * Die Kopfzone der Werkbank fuer alle Nebenzweige (Laden, Fehler,
   * Leerzustaende).
   *
   * Die Kopfzeile traegt HIER dieselbe Struktur und dieselben Klassen wie im
   * Hauptzweig unten — nicht aus Ordnungsliebe: nur wenn ein Element an
   * derselben Baumposition steht, aktualisiert React den Knoten, statt ihn
   * auszutauschen, und nur dann ueberlebt der Fokus den Zweigwechsel. Genau
   * daran hing der V3-Befund bei den Reitern.
   *
   * `mitFilterleiste` zieht die Leiste in dieselbe Zeile. Gesetzt wird das
   * bislang nur vom Lade-Zweig: dort stand die Leiste als Skelett daneben,
   * das Suchfeld wurde also ausgehaengt und der Fokus fiel mitten im Tippen
   * auf <body>. Fehler- und Leerzustaende bleiben bewusst ohne Leiste — sie
   * bringen ihre eigene, benannte Handlung mit ("Suche zuruecksetzen",
   * "Filter zuruecksetzen"), und eine zweite Bedienung fuer dieselbe Sache
   * daneben waere kein Gewinn.
   *
   * Mit der Leiste kommen `aktionen` mit, und das ist Absicht: sie stehen im
   * Hauptzweig an genau dieser Stelle, sind selbst fuer ihren Zustand
   * zustaendig (beide fragen ihre Zahlen eigenstaendig ab) und blieben sonst
   * dieselbe Falle eine Tuer weiter — wer den Freigeben-Knopf fokussiert hat
   * und in den Lade-Zweig geraet, verloere den Fokus. Nebeneffekt: sie werden
   * ueber den Zweigwechsel nicht mehr neu montiert und fragen ihre Zahlen
   * nicht erneut ab.
   */
  const withStatusTabs = (content: ReactNode, opts: { mitFilterleiste?: boolean } = {}) => (
    <div className="flex flex-col h-full min-h-0 gap-4">
      <div className={eineZeile ? 'flex-shrink-0 flex flex-wrap items-center gap-3' : 'flex-shrink-0'}>
        <WorkbenchStatusTabs active={status} counts={statusCounts} onChange={requestStatus} />
        {eineZeile && opts.mitFilterleiste && (
          <>
            <WorkbenchFilterBar
              kompakt
              filters={filters}
              onFiltersChange={requestFilters}
              railWidth={railWidth}
              onRailWidthChange={setRailWidth}
            />
            <div className="flex-1" />
            {aktionen && <div className="flex flex-shrink-0 items-center gap-2">{aktionen}</div>}
          </>
        )}
      </div>
      {/* Das Gegenstueck zum Tablist (V3-Befund: es gab keins — der
          Screenreader wusste nicht, was die Reiter steuern). Dieselben
          Attribute traegt der Hauptzweig unten. */}
      <div
        role="tabpanel"
        id="workbench-tabpanel"
        aria-labelledby={`workbench-tab-${status}`}
        /* `gap-4` NUR mit Leiste: sie braucht den Abstand zum Inhalt
           darunter. Ohne sie traegt dieser Knoten in JEDEM Zweig genau ein
           Kind — auch im Fehlerzweig, dessen zwei Elemente eine Ebene tiefer
           in einem eigenen Container liegen, den ein Abstand hier gar nicht
           erreicht. Unbedingt gesetzt waere der Wert also folgenlos; bedingt
           gesetzt sagt er, wofuer er da ist. */
        className={`flex-1 min-h-0 flex flex-col${opts.mitFilterleiste ? ' gap-4' : ''}`}
      >
        {!eineZeile && opts.mitFilterleiste && (
          <div className="flex-shrink-0">
            <WorkbenchFilterBar
              filters={filters}
              onFiltersChange={requestFilters}
              railWidth={railWidth}
              onRailWidthChange={setRailWidth}
            />
          </div>
        )}
        {content}
      </div>
    </div>
  );

  if (isLoading) {
    // V3-Befund: die Reiter fehlten AUSGERECHNET hier. Jeder Reiterwechsel
    // laeuft kurz durch diesen Zweig — die Reiter verschwanden fuer einen
    // Lade-Frame, wurden danach neu montiert, und der Fokus fiel auf <body>.
    // Der halbe Roving Tabindex aus WorkbenchStatusTabs war damit im echten
    // Betrieb wirkungslos, obwohl der Komponententest gruen war (der gemockte
    // Hook laedt nie). withStatusTabs haelt die Reiter an derselben
    // Baumposition wie in allen anderen Zweigen; React aktualisiert die
    // Knoten dann, statt sie auszutauschen, und der Fokus ueberlebt.
    //
    // Nachtrag 09.09.2026, derselbe Befund eine Zeile tiefer: die Filterleiste
    // stand hier als SKELETT: vier graue Balken an der Stelle des Suchfelds.
    // Also wurde auch das Suchfeld ausgehaengt, und der Fokus fiel mitten im
    // Tippen auf <body>. Sie kommt jetzt ueber `mitFilterleiste` aus derselben
    // Quelle wie im Hauptzweig; Skelett bleibt fuer das, was wirklich laedt:
    // Fortschritt und Karte.
    return withStatusTabs(
      <div className="flex flex-col h-full min-h-0 gap-4">
        {/* Progress bar */}
        <div className="flex-shrink-0 space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>

        {/* Product card — header + before/after columns */}
        <div className="flex-1 min-h-0 rounded-lg border bg-card overflow-hidden flex flex-col">
          <div className="flex-shrink-0 flex items-start gap-4 p-4 border-b border-border">
            <Skeleton className="h-16 w-16 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-2/3" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
            <div className="p-4 space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-20 w-full" />
            </div>
            <div className="p-4 space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </div>
      </div>,
      { mitFilterleiste: true },
    );
  }

  if ((error || isPaused) && products.length === 0) {
    // Auch der Fehlerzweig behaelt die Reiter: ein Fehler in EINEM Reiter
    // laesst die anderen zwei erreichbar — ohne Reiter gaebe es aus dem
    // Fehler keinen Weg mehr heraus ausser dem Neuladen.
    //
    // H6-F4 (E2E-Sweep 25.08.): `isPaused` steht hier GLEICHBERECHTIGT neben
    // `error`. Offline pausiert React Query die Abfrage (networkMode
    // 'online') statt sie fehlschlagen zu lassen — error bleibt null,
    // isLoading false, products leer. Ohne diesen Zweig fiel genau dieser
    // Zustand in die Leerzustands-Gates darunter, und die Werkbank meldete
    // "Nothing approved yet" fuer einen Feed mit freigegebenen Produkten.
    // Ein Leerzustand, der luegt; der Fehlerzustand mit Retry ist die
    // ehrliche Antwort (Muster WorkbenchReleaseMenu, K5-Lehre). Der Retry
    // ist auch im Pausen-Fall richtig: kommt die Verbindung zurueck, nimmt
    // React Query pausierte Abfragen ohnehin wieder auf.
    //
    // K5-Befund, zweifach: (1) Der Zweig greift NUR noch, wenn wirklich
    // keine Daten da sind. Ein fehlgeschlagener FOLGESEITEN-Abruf (vom
    // Vorlader angestossen, vom Nutzer nie angefordert) ersetzte sonst die
    // komplette funktionierende Werkbank — Karte samt offenem Anpassen-Feld —
    // durch den Vollflaechen-Fehler, obwohl alle geladenen Seiten intakt im
    // Cache lagen. Mit Daten faellt der Fehler jetzt durch: die geladenen
    // Produkte bleiben pruefbar, "Mehr laden" ist der manuelle Retry.
    // (2) Mit aktiver Suche gibt es einen Rueckweg: eine Suche, die
    // serverseitig ins statement_timeout laeuft, liess sich sonst nur per
    // Seiten-Reload verlassen (die Filterleiste rendert hier nicht, und der
    // Suchbegriff ueberlebt Reiter- wie Feedwechsel).
    return withStatusTabs(
      <div className="flex-1 flex flex-col">
        <ErrorState onRetry={() => refetch()} />
        {filters.search.trim() !== '' && (
          <div className="flex justify-center pb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => requestFilters({ ...filters, search: '' })}
            >
              {t('errorResetSearch')}
            </Button>
          </div>
        )}
      </div>,
    );
  }

  // Zwei Zustaende, die von aussen gleich aussehen: nichts zu pruefen, kein
  // Filter. Entweder ist wirklich nichts optimiert — oder alles ist bereits
  // freigegeben und wartet auf den Upload (so kommt man ueber Schritt 4 der
  // Fortschrittsleiste hier an, und genauso ueber eine vollstaendige
  // Sammelfreigabe). Die Unterscheidung braucht eine Zahl, die der Reviewer
  // selbst nicht hat; sie kommt aus der Feed-Statistik der Seite — nicht aus
  // den Sitzungszaehlern.
  //
  // Die Zaehler standen bis hierher mit `=== 0` IM Gate und haben genau die
  // vollstaendige Sammelfreigabe herausgeworfen (Begruendung an
  // isNothingToReviewEmptyState): danach ist `total` 0, keine Zeile geladen,
  // aber die Zaehler stehen auf der Zahl der Freigaben. Der Nutzer landete
  // stattdessen beim "Noch nichts zu pruefen"-Hinweis, dessen Primaerknopf zum
  // Feed-Check und damit vom Upload weg fuehrt.
  if (isNothingToReviewEmptyState({ total, hasActiveFilters: hasActiveFilters(filters) })) {
    // Task A7: In den Reitern "Freigegeben" und "Abgelehnt" heisst leer etwas
    // ganz anderes als im Reiter "Offen". Dort waeren beide bisherigen Texte
    // falsch: "Noch nichts zu pruefen" schickt zum Feed-Check (also vom Ziel
    // weg), und "alles freigegeben, wartet auf Upload" ist im Reiter
    // "Abgelehnt" schlicht keine Aussage ueber das, was der Nutzer ansieht.
    if (status === 'approved') return withStatusTabs(<WorkbenchEmptyState variant="no-approved" />);
    if (status === 'rejected') return withStatusTabs(<WorkbenchEmptyState variant="no-rejected" />);

    return withStatusTabs(
      <WorkbenchEmptyState
        variant={approvedAwaitingUpload > 0 ? 'approved-awaiting-upload' : 'no-optimized'}
        approvedAwaitingUpload={approvedAwaitingUpload}
        onUpload={onUpload}
        justBulkReleased={zuletztSammelFreigegeben}
        approvedTotal={statusCounts?.approved ?? null}
        avgScore={feedAvgScore}
      />,
    );
  }

  // Die volle Begruendung steht am Doc-Kommentar von isAllReviewedEmptyState.
  // Kurz: gefragt wird "ist jede GELADENE Zeile entschieden, und kann es keine
  // weiteren geben?". Die Sitzungszaehler gehen NICHT mehr in die Entscheidung
  // ein (Produktionsfehler: sie ueberleben den Ansichtswechsel, die geladene
  // Menge nicht — der Zustand feuerte bei offenen Produkten), sie beschriften
  // den Leerzustand nur noch. `mayHaveMore` haelt F3 fest. `products.length`
  // statt `total`: `total` traegt im Kartenpfad den bis zu 30 Sekunden alten
  // `serverTotal`, und auf dem allein hat der Zustand gefeuert, obwohl gar
  // keine Zeile geladen war — der Zwilling `approved-awaiting-upload` oben
  // kam dann nie zum Zug (siehe Doc-Kommentar von isAllReviewedEmptyState).
  if (
    isAllReviewedEmptyState({
      loadedCount: products.length,
      undecidedLoadedCount,
      hasActiveFilters: hasActiveFilters(filters),
      mayHaveMore,
    })
  ) {
    return withStatusTabs(
      <WorkbenchEmptyState
        variant="all-reviewed"
        sessionAccepted={sessionAccepted}
        sessionRejected={sessionRejected}
        approvedAwaitingUpload={approvedAwaitingUpload}
        onUpload={onUpload}
      />,
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 gap-4">
      {/* Die Reiter stehen UEBER der Filterleiste, weil sie die Menge
          bestimmen und die Filter sie nur verfeinern. Andersherum angeordnet
          liesse sich nicht mehr sagen, worauf sich eine Kopfzahl bezieht.
          Der Reiterwechsel setzt den Puffer von selbst zurueck, weil `status`
          Teil der Puffer-Population ist (useWorkbench.ts); eine offene
          Bearbeitung schreibt der Auto-Save beim Produktwechsel vorher weg. */}
      {/* EINE Zeile statt drei Bloecken. `WorkbenchStatusTabs` bringt
          `role="tablist"` und seinen Pfeiltasten-Handler selbst mit; beide
          bleiben AN DER KOMPONENTE und wandern nicht auf diesen Container.
          Laege der Handler hier, verloere das Suchfeld daneben seine
          Cursorbewegung (er ruft preventDefault fuer Pfeil links/rechts,
          Pos1 und Ende), und der globale Blaetter-Handler koennte einen
          Pfeildruck nicht mehr von einem Reiterwechsel unterscheiden. */}
      <div className={eineZeile ? 'flex-shrink-0 flex flex-wrap items-center gap-3' : 'flex-shrink-0'}>
        <WorkbenchStatusTabs active={status} counts={statusCounts} onChange={requestStatus} />
        {eineZeile && (
          <>
            {/* Verdichtet, nicht bloss umgehaengt. Die ausgeschriebene Leiste
                belegt 1189 px; neben Reitern und Aktionen bleiben ihr selbst
                bei 1500 px Container rund 950, und sie bricht um - die "eine
                Zeile" war dann in JEDER Fensterbreite drei Zeilen hoch.
                Gemessen, nicht vermutet: genau dieser Fehler stand kurz in
                der Produktion. */}
            <WorkbenchFilterBar
              kompakt
              filters={filters}
              onFiltersChange={requestFilters}
              railWidth={railWidth}
              onRailWidthChange={setRailWidth}
            />
            <div className="flex-1" />
            {aktionen && <div className="flex flex-shrink-0 items-center gap-2">{aktionen}</div>}
          </>
        )}
      </div>

      {/* Siehe withStatusTabs: das Tabpanel-Gegenstueck des Hauptzweigs. */}
      <div
        role="tabpanel"
        id="workbench-tabpanel"
        aria-labelledby={`workbench-tab-${status}`}
        className="flex flex-1 min-h-0 flex-col gap-4"
      >
      {!eineZeile && (
      <div className="flex-shrink-0">
        <WorkbenchFilterBar
          filters={filters}
          onFiltersChange={requestFilters}
          railWidth={railWidth}
          onRailWidthChange={setRailWidth}
        />
      </div>
      )}

      {/* `total > 0`: die Leiste haengt an einer gemessenen Menge, nicht nur an
          der Ansicht. Ohne das Gate stand ueber dem leergefilterten Kartenmodus
          woertlich "Produkt 1 von 0" (und "3/0 geprueft" nach Freigaben) — der
          filterlose total===0-Fall steigt schon oben aus, mit aktivem Filter
          erreicht er diese Zeile. */}
      {railWidth !== 'wide' && total > 0 && fortschritt === 'oben' && (
        <div className="flex-shrink-0">
          <WorkbenchProgress
            current={currentIndex}
            total={total}
            totalUnknown={serverTotalUnknown}
            sessionAccepted={sessionAccepted}
            sessionRejected={sessionRejected}
          />
        </div>
      )}

      {/* Review-Fund F3, zweiter Fall (isNoProductsEmptyState in
          useWorkbench.ts): bei aktivem Score-Filter kann die aktuell
          geladene Seite komplett leergefiltert sein (0 Treffer), obwohl der
          Server noch weitere Seiten hat. WorkbenchListView triggert dann
          selbst ein Nachladen fuer eine leere Seite (siehe dortiger Effect);
          im Kartenmodus uebernimmt das der F2-Nachlade-Effect in
          useWorkbench.ts. */}
      {isNoProductsEmptyState({ total, hasNextPage }) ? (
        <div className="flex-1 flex items-center justify-center">
          {/* Mit aktivem Filter ist "Noch nichts zu pruefen — optimiere deine
              Produkte" doppelt falsch: es kann tausende offene Produkte geben,
              nur passt keines zur Suche bzw. Score-Pille — und der
              'no-optimized'-Primaerknopf fuehrte aus der Werkbank hinaus.
              Wahre Aussage + einzige Handlung "Filter zuruecksetzen". Das
              Signal ist der Filter-State selbst, keine Sitzungszaehler. */}
          {hasActiveFilters(filters) ? (
            <WorkbenchEmptyState
              variant="no-filter-results"
              onResetFilters={() => requestFilters(DEFAULT_FILTERS)}
            />
          ) : (
            <WorkbenchEmptyState variant="no-optimized" />
          )}
        </div>
      ) : railWidth === 'wide' ? (
        <div className="flex-1 min-h-0">
          <WorkbenchListView
            products={products}
            /* Spec B.2: der Zeilenklick wirkt auch in der vollen Breite —
               Zeile klicken, Leiste geht auf `normal`, Produkt steht rechts.
               Ohne das bliebe die Luecke "kein Weg von der Liste ins Produkt"
               ausgerechnet in der breitesten Ansicht bestehen. Ueber
               `requestCursor`, nicht ueber `goToProduct`: sonst faellt die
               Speicher-Reihenfolge aus Task B4 weg. */
            onOpenProduct={(id) => {
              void requestCursor(id);
              setRailWidth('normal');
            }}
            getReviewDecision={getReviewDecision}
            wasEdited={wasEdited}
            onBulkApprove={guardedBulkApprove}
            onBulkReject={guardedBulkReject}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onFetchNextPage={fetchNextPage}
            loadError={Boolean(error)}
            loadedCount={listLoadedCount}
            pendingTotal={listPendingTotal}
            autoLoadCapReached={autoLoadCapReached}
            onUnlockMoreAutoLoad={unlockMoreAutoLoad}
            /* Der Prop heisst nach der Punkte-Pille, gefragt ist aber
               allgemeiner: verengt IRGENDEIN clientseitiger Filter `products`
               unter die roh geladene Menge? Dann darf `aria-rowcount` nicht
               mehr die feed-weite Zahl behaupten. `warningsOnly` tut genau
               das und muss deshalb mit hinein. Umbenannt wird der Prop hier
               nicht — das beruehrte Liste und Tests ohne Gewinn an Wahrheit. */
            hasActiveScoreFilter={filters.scoreFilter !== 'all' || filters.warningsOnly}
            /* V3-Befund: in "Freigegeben"/"Abgelehnt" ist nichts auswaehlbar —
               ohne dieses Gate stand dort "0 Produkte zu pruefen" neben zwei
               dauerhaft gesperrten Sammelknoepfen, deren Hinweis eine
               unmoegliche Handlung verlangte. */
            bulkActionsEnabled={status === 'pending'}
          />
        </div>
      ) : currentProduct ? (
        <>
          {/* Task B4: geteilte Ansicht. In `hidden` bleibt es exakt die alte
              Karte, in `normal` steht die Leiste daneben. Der Klick dort
              laeuft ueber `requestCursor` — nie direkt auf `goToProduct`,
              sonst faellt die Speicher-Reihenfolge weg. */}
          {(() => {
            const showRail =
              railWidth === 'normal' && (!isNarrow || narrowLevel === 'rail');
            // Schmal: entweder Leiste ODER Detail. Breit: beides. Bei
            // `hidden` gibt es keine Leiste und damit auch keine Ebene, aus
            // der man zurueckkehren koennte.
            const showDetail = !isNarrow || railWidth !== 'normal' || narrowLevel === 'detail';

            return (
              <div className={cn('flex flex-1 min-h-0', eigeneKachel ? 'gap-4' : 'gap-0')}>
                {showRail && (
                  /* Die Kachel-Klassen gehen an den BESTEHENDEN Knoten, nicht
                     an einen neuen Wrapper darum: die Rail-Wurzel arbeitet mit
                     h-full ohne eigene Hoehe und funktioniert nur als
                     Streckkind dieses Flex-Containers. Ein unklassierter
                     Zwischenknoten loeste das h-full still gegen height:auto
                     auf, und der virtualisierte Scroller kollabierte - in der
                     Testsuite unsichtbar, weil jsdom keine Layouts misst und
                     der Virtualizer gemockt ist.

                     `overflow-hidden` ist Pflicht, sonst schneidet der
                     Scrollbalken des inneren Scrollers die Rundung an. Die
                     Produktkarte daneben macht es an derselben Stelle
                     genauso. */
                  <div
                    data-testid="rail-container"
                    className={cn(
                      isNarrow ? 'flex-1 min-w-0' : 'flex-shrink-0',
                      eigeneKachel &&
                        'flex flex-col min-h-0 overflow-hidden rounded-lg border bg-card shadow-sm',
                    )}
                    /* Befoerderungs-Review-Fund 24.08.: `leistenBreite` war ein
                       totes Feld - die Breite stand hartkodiert auf w-[300px],
                       der Schalter (und Dennis' 340er-Wahl) hatte keine
                       Wirkung. Inline-Style statt Klasse, weil Tailwind keine
                       dynamischen Breiten kompiliert. */
                    style={isNarrow ? undefined : { width: leistenBreite }}
                  >
                    <WorkbenchRail
                      products={products}
                      currentId={currentProduct.id}
                      onSelect={requestCursor}
                      selectedIds={railSelectedIds}
                      onToggleSelect={toggleRailSelection}
                      getReviewDecision={getReviewDecision}
                      loadedCount={listLoadedCount}
                      pendingTotal={listPendingTotal}
                      hasNextPage={hasNextPage}
                      onLoadMore={fetchNextPage}
                      /* `isNarrow` ist hier entfallen: seit Spec 9.4 haengen
                         Auswahlboxen und Kopf-Schalter allein am
                         Auswahlmodus, in jeder Breite. Die Breite entscheidet
                         weiter ueber die Flaeche (`showRail`/`showDetail` und
                         die Container-Klasse oben), nicht mehr ueber die
                         Bedienung. */
                      selectionMode={railSelectionMode}
                      onSelectionModeChange={setRailSelectionModeAndClear}
                      onBulkApprove={handleRailBulkApprove}
                      onBulkReject={handleRailBulkReject}
                    />
                  </div>
                )}

                {showDetail && (
                  <div className="flex min-w-0 flex-1 flex-col">
                    {isNarrow && railWidth === 'normal' && (
                      /* Der Rueckweg existiert nur schmal und nur dort, wo es
                         eine Listenebene gibt. Ein Knopf, der breit bloss
                         ausgeblendet waere, bliebe fuer Tastatur und
                         Screenreader erreichbar und fuehrte ins Leere. */
                      <div className="flex-shrink-0 border-b border-border p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setNarrowLevel('rail')}
                          className="h-8 gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          {t('actions.backToList')}
                        </Button>
                      </div>
                    )}
                    <div className="min-h-0 flex-1">
                      <WorkbenchProductCard
                        product={currentProduct}
                        editState={editState}
                        reviewDecision={currentDecision}
                        /* Etappe 5: die VEREINIGUNG beider Signale. Keines
                           traegt die Marke allein - `currentWasEdited` wird
                           erst nach dem DB-Write wahr (Schreiber ist
                           `markEdited` am Ende von `saveChanges`), und der
                           Tipp-Vergleich faellt nach dem Auto-Save wieder auf
                           falsch, weil `originalEditState` nachgezogen wird,
                           obwohl der Handstand besteht.

                           Die lebende Haelfte vergleicht bewusst NUR den
                           Titel, nicht `hasUnsavedChanges` (das prueft alle
                           zehn Felder): die Marke sitzt zwei Zeilen ueber dem
                           Titeltext und wird als Aussage ueber DIESEN Text
                           gelesen. Ein Zeichen in der Beschreibung haette sie
                           sonst ueber einem Titel aufleuchten lassen, den
                           niemand angefasst hat - eine falsche Herkunftsangabe
                           auf der Flaeche, die die Freigabe traegt.
                           `currentWasEdited` bleibt produktweit; enger geht es
                           ohne eine feldgenaue Fassung von `editedSet` nicht. */
                        hasManualEdit={
                          currentWasEdited ||
                          editState.optimized_title !== originalEditState.optimized_title
                        }
                        onEditChange={handleEditChange}
                        position={currentIndex + 1}
                        total={total}
                        totalUnknown={serverTotalUnknown}
                        /* Etappe 3: die Entscheidungsleiste sitzt am Fuss DER
                           KARTE. Als Knoten hereingereicht statt in der Karte
                           gebaut, damit die Karte ihr Layout besitzt und der
                           Reviewer seine Handler behaelt. */
                        footer={
                          <WorkbenchActionBar
                            canGoPrev={canGoPrev}
                            canGoNext={canGoNext}
                            isAccepting={isAccepting}
                            isRejecting={isRejecting}
                            isCurrentReviewed={isCurrentReviewed}
                            status={status}
                            onPrev={requestPrev}
                            onNext={requestNext}
                            onAccept={handleAccept}
                            onReject={handleReject}
                            onEdit={handleEditFromActionBar}
                            onWithdrawApproval={handleReopen}
                            onReopen={handleReopen}
                            onUndoLast={handleUndoLast}
                            canUndoLast={lastDecidedId !== null}
                          />
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Review-Runde 3, Fund N3: dieser Zweig fehlte komplett — die CTA
              war an `currentProduct == null` gekoppelt (via der `: hasNextPage
              && autoLoadCapReached ? ...` Alternative unten), also NIE
              sichtbar, solange der Filter Treffer hat. Im score-gefilterten
              Kartenmodus mit Treffern endete "Weiter" bei `canGoNext=false`
              still, ohne Erklaerung, dass der Deckel (nicht das Feed-Ende)
              der Grund war. `!canGoNext` ist hier "am Ende des Gefilterten
              angekommen"; `isAutoLoadCtaVisible` ist im windowed Standard-
              Kartenmodus (kein Filter) immer false (siehe deren Doc-Kommentar
              zu hasNextPage/autoLoadCapReached), zeigt sich also nur dort, wo
              der Deckel ueberhaupt greifen kann. */}
          {/* K6-Befund: der seit Nr. 74 durchfallende Folgeseiten-Fehler war im
              Kartenpfad komplett stumm — kein Signal, kein Retry, und in
              Breite 'hidden' gar kein Weg zurueck. Der Hinweis erscheint an
              der geklemmten Navigationsgrenze; Retry stoesst den Abruf neu an
              (Erfolg heilt error und weckt damit auch den Vorlader wieder). */}
          {Boolean(error) && products.length > 0 && hasNextPage && (
            <div className="flex-shrink-0 flex flex-col items-center justify-center gap-2 px-4 py-3 border-t border-border bg-muted/30 text-center">
              <p className="text-sm text-destructive-ink">{t('listLoadFailed')}</p>
              <Button variant="outline" size="sm" onClick={() => fetchNextPage()}>
                {t('listLoadFailedRetry')}
              </Button>
            </div>
          )}
          {!canGoNext && isAutoLoadCtaVisible(autoLoadCapReached, hasNextPage) && (
            <div className="flex-shrink-0 flex flex-col items-center justify-center gap-2 px-4 py-3 border-t border-border bg-muted/30 text-center">
              <p className="text-sm text-muted-foreground">
                {t('listAutoLoadCappedMore', { count: listLoadedCount })}
              </p>
              <Button variant="outline" size="sm" onClick={() => unlockMoreAutoLoad()}>
                {t('listAutoLoadCappedMoreAction')}
              </Button>
            </div>
          )}

        </>
      ) : isAutoLoadCtaVisible(autoLoadCapReached, hasNextPage) ? (
        // Review-Runde 2, Fund N1: score-gefilterter Kartenmodus, aktuelle
        // Seite(n) leergefiltert (kein currentProduct), UND der F2-Effect in
        // useWorkbench.ts hat am Deckel (AUTO_LOAD_ROW_CAP) aufgehoert, weiter
        // zu scannen. Ehrlicher Hinweis mit Handlungsangebot statt eines
        // endlosen Platzhalters.
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('listAutoLoadCapped', { count: listLoadedCount })}
          </p>
          <Button variant="outline" size="sm" onClick={() => unlockMoreAutoLoad()}>
            {t('listAutoLoadCappedAction')}
          </Button>
        </div>
      ) : hasNextPage ? (
        // Konsequenz aus F3/F2: score-gefilterter Kartenmodus, aktuelle Seite(n)
        // leergefiltert, aber der F2-Effect in useWorkbench.ts laedt bereits
        // nach (noch unter dem Deckel). Kurzer Platzhalter statt eines leeren
        // Bildschirms, bis genug Seiten geladen sind, dass der Filter wieder
        // etwas zeigt.
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="h-8 w-48 rounded-md" />
        </div>
      ) : null}
      </div>
    </div>
  );
}
